import { useEffect, useState } from "react";
import { X, Download, ExternalLink, ChevronLeft, ChevronRight, Loader2, File as FileIcon } from "lucide-react";
import { api, API } from "@/lib/api";
import { fmtBytes, fmtTid, vedleggKategori } from "./sakerUtils";

const FilKort = ({ att, url }) => (
  <div className="bg-white rounded-xl px-10 py-12 text-center max-w-[380px] shadow-2xl" data-testid="vedlegg-viser-filkort">
    <FileIcon size={30} strokeWidth={1.4} className="mx-auto text-neutral-300" />
    <p className="mt-4 text-[14px] font-semibold text-neutral-900 break-all">{att.name}</p>
    <p className="mt-1 text-[11.5px] text-neutral-400">{fmtBytes(att.size)} · ingen forhåndsvisning for denne filtypen</p>
    <a
      href={`${url}?nedlast=1`}
      className="mt-5 inline-flex items-center gap-2 text-[12.5px] font-medium bg-neutral-900 text-white rounded-lg px-4 py-2 hover:bg-neutral-700 transition-colors"
      data-testid="vedlegg-viser-nedlast-kort"
    >
      <Download size={13} /> Last ned
    </a>
  </div>
);

export default function VedleggViser({ sak, vedlegg, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [innhold, setInnhold] = useState(null);
  const [laster, setLaster] = useState(false);
  const att = vedlegg[Math.min(idx, vedlegg.length - 1)];
  const kat = vedleggKategori(att);
  const erDocx = String(att.name || "").toLowerCase().endsWith(".docx");
  const url = `${API}/admin/tasks/${sak.id}/attachments/${att.id}/file`;
  const flere = vedlegg.length > 1;

  useEffect(() => {
    setInnhold(null);
    if (erDocx || kat === "tekst") {
      setLaster(true);
      api.get(`/admin/tasks/${sak.id}/attachments/${att.id}/preview`)
        .then(({ data }) => setInnhold(data))
        .catch(() => setInnhold({ type: "feil" }))
        .finally(() => setLaster(false));
    }
  }, [sak.id, att.id, erDocx, kat]);

  useEffect(() => {
    const tast = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowRight" && flere) setIdx((i) => (i + 1) % vedlegg.length);
      if (e.key === "ArrowLeft" && flere) setIdx((i) => (i - 1 + vedlegg.length) % vedlegg.length);
    };
    window.addEventListener("keydown", tast, true);
    return () => window.removeEventListener("keydown", tast, true);
  }, [onClose, flere, vedlegg.length]);

  let visning;
  if (kat === "pdf") {
    visning = <iframe src={url} title={att.name} className="w-full h-full max-w-[1100px] bg-white rounded-lg shadow-2xl" data-testid="vedlegg-viser-pdf" />;
  } else if (kat === "bilde") {
    visning = <img src={url} alt={att.name} className="max-w-full max-h-full object-contain rounded-md shadow-2xl" data-testid="vedlegg-viser-bilde" />;
  } else if ((erDocx || kat === "tekst") && laster) {
    visning = <div className="flex flex-col items-center gap-3 text-neutral-400"><Loader2 size={22} className="animate-spin" /><span className="text-[12px]">Klargjør forhåndsvisning …</span></div>;
  } else if (erDocx && innhold?.type === "html") {
    visning = <div className="vedlegg-papir" dangerouslySetInnerHTML={{ __html: innhold.html }} data-testid="vedlegg-viser-word" />;
  } else if (kat === "tekst" && innhold?.type === "text") {
    visning = <pre className="vedlegg-tekst" data-testid="vedlegg-viser-tekst">{innhold.text}</pre>;
  } else {
    visning = <FilKort att={att} url={url} />;
  }

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" data-testid="vedlegg-viser">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />

      <div className="relative flex items-center gap-3 px-4 sm:px-6 py-3 shrink-0">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-white truncate" data-testid="vedlegg-viser-tittel">{att.name}</p>
          <p className="text-[10.5px] text-white/40">{fmtBytes(att.size)} · {att.uploadedBy} · {fmtTid(att.at)}</p>
        </div>
        <a href={`${url}?nedlast=1`} className="p-2 text-white/60 hover:text-white transition-colors" title="Last ned" data-testid="vedlegg-viser-nedlast">
          <Download size={16} />
        </a>
        <a href={url} target="_blank" rel="noreferrer" className="hidden sm:block p-2 text-white/60 hover:text-white transition-colors" title="Åpne i ny fane" data-testid="vedlegg-viser-nyfane">
          <ExternalLink size={16} />
        </a>
        <button type="button" onClick={onClose} className="p-2 text-white/60 hover:text-white transition-colors" title="Lukk (Esc)" data-testid="vedlegg-viser-lukk">
          <X size={18} />
        </button>
      </div>

      <div className="relative flex-1 min-h-0 flex items-center justify-center px-3 sm:px-16 pb-5">
        {flere && (
          <>
            <button
              type="button"
              onClick={() => setIdx((i) => (i - 1 + vedlegg.length) % vedlegg.length)}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors z-10"
              data-testid="vedlegg-viser-forrige"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => (i + 1) % vedlegg.length)}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors z-10"
              data-testid="vedlegg-viser-neste"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
        {visning}
      </div>

      {flere && (
        <p className="relative text-center pb-3 bud-mono text-[10.5px] tabular-nums text-white/40 shrink-0" data-testid="vedlegg-viser-teller">
          {idx + 1} / {vedlegg.length}
        </p>
      )}
    </div>
  );
}
